export const AUTH_BASE_URL = 'https://netflixsubs.app';

/** Patreon OAuth (secondary). Primary path is email verification code. */
export const PATREON_CLIENT_ID =
  'BsOi6oolQ92ta58URZt3FxPkA2E-zTUSKoRJ5Npo2KN6kJrWWMXlvABemmB353Ez';
/**
 * Same redirect as the Chrome extension — already registered on the Patreon V2 app.
 * Do not use mobile.origin/login/callback; that would need a separate Patreon URI.
 * Credentials return via www callback HTML → opener postMessage (and extension DOM pickup).
 */
export const PATREON_REDIRECT_URI =
  'https://www.netflixsubs.app/v2/auth/patreon/callback';

/** Always the extension/www callback (origin arg kept for call-site compatibility). */
export function resolvePatreonRedirectUri(_origin?: string): string {
  return PATREON_REDIRECT_URI;
}

export function buildPatreonAuthorizeUrl(
  redirectUri: string = PATREON_REDIRECT_URI,
): string {
  return (
    `https://www.patreon.com/oauth2/authorize?response_type=code`
    + `&client_id=${PATREON_CLIENT_ID}`
    + `&redirect_uri=${encodeURIComponent(redirectUri)}`
    + `&scope=identity`
  );
}

export type AuthApiOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

export type AuthApiError = {
  code: string;
  message: string;
};

export type SendCodeResult =
  | { ok: true }
  | { ok: false; error: AuthApiError };

export type SingleLoginResult =
  | { ok: true; userId: string; userToken: string }
  | { ok: false; error: AuthApiError };

export type ValidateResult = {
  /** Normalized status object stored as statusJson. */
  status: { response: string; ok?: boolean; data?: unknown; error?: unknown };
  statusJson: string;
  unlock: boolean;
};

function parseError(json: {
  ok?: boolean;
  error?: { code?: string; message?: string };
}): AuthApiError {
  return {
    code: json.error?.code ?? 'failed',
    message: json.error?.message ?? 'Request failed',
  };
}

export async function sendLoginCode(
  email: string,
  options: AuthApiOptions & { languageCode?: string; appName?: string } = {},
): Promise<SendCodeResult> {
  const base = (options.baseUrl ?? AUTH_BASE_URL).replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  try {
    const res = await fetchImpl(`${base}/v2/auth/send-code`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        languageCode: options.languageCode ?? navigator.language,
        appName: options.appName ?? 'Youtube Bilingual Subtitles',
      }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      error?: { code?: string; message?: string };
    };
    if (json.ok === true) return { ok: true };
    return { ok: false, error: parseError(json) };
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network or server error' } };
  }
}

export async function loginWithCode(
  code: string,
  options: AuthApiOptions = {},
): Promise<SingleLoginResult> {
  const base = (options.baseUrl ?? AUTH_BASE_URL).replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  try {
    const res = await fetchImpl(`${base}/v2/auth/single-login`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code: code.replace(/\s+/g, '').trim() }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      data?: { uid?: string; token?: string };
      error?: { code?: string; message?: string };
    };
    if (json.ok === true && json.data?.uid && json.data?.token) {
      return { ok: true, userId: json.data.uid, userToken: json.data.token };
    }
    return { ok: false, error: parseError(json) };
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network or server error' } };
  }
}

/**
 * Validate membership. Stores a normalized status shape compatible with
 * applyMembershipUnlockFromStatus (OK | Session Expired | Not Found | Failed).
 * Never logs tokens. Unlock only on OK.
 */
export async function validateMembership(
  userId: string,
  userToken: string,
  options: AuthApiOptions = {},
): Promise<ValidateResult> {
  const base = (options.baseUrl ?? AUTH_BASE_URL).replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  const q = new URLSearchParams({ uid: userId });
  try {
    const res = await fetchImpl(`${base}/v2/auth/validate?${q}`, {
      headers: {
        Accept: 'application/json',
        ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
      },
    });
    const json = (await res.json()) as {
      ok?: boolean;
      data?: unknown;
      error?: { code?: string; message?: string; reason?: string };
    };

    let status: ValidateResult['status'];
    const reason = json.error?.reason;
    const code = json.error?.code;
    if (json.ok === true) {
      status = { response: 'OK', ok: true, data: json.data };
    } else if (
      json.ok === false
      && (reason === 'session_expired' || code === 'session_expired')
    ) {
      status = { response: 'Session Expired', ok: false, error: json.error };
    } else if (
      json.ok === false
      && (
        reason === 'membership_inactive'
        || code === 'membership_inactive'
        || code === 'unauthorized'
      )
    ) {
      status = { response: 'Not Found', ok: false, error: json.error };
    } else if (json.ok === false) {
      status = { response: 'Failed', ok: false, error: json.error };
    } else {
      status = { response: 'Failed' };
    }

    const statusJson = JSON.stringify(status);
    return {
      status,
      statusJson,
      unlock: status.response === 'OK' || status.ok === true,
    };
  } catch {
    const status = { response: 'Failed' as const };
    return { status, statusJson: JSON.stringify(status), unlock: false };
  }
}
